import React, { useState, useEffect } from 'react';
import { Save, X, Plus, Trash2 } from 'lucide-react';
import { EmailBlock } from '../types';
import { serializeDesign, deserializeDesign } from '../utils/blockSerializer';

interface ReusableHeader {
  _id?: string;
  name: string;
  headerBlock: EmailBlock;
}

interface ReusableHeaderManagerProps {
  currentHeaderBlock: EmailBlock | null;
  onSelectHeader: (headerBlock: EmailBlock) => void;
  isDarkMode: boolean;
}

export const ReusableHeaderManager: React.FC<ReusableHeaderManagerProps> = ({
  currentHeaderBlock,
  onSelectHeader,
  isDarkMode
}) => {
  const [savedHeaders, setSavedHeaders] = useState<ReusableHeader[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [headerName, setHeaderName] = useState('');
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    loadSavedHeaders();
  }, []);

  const loadSavedHeaders = async () => {
    try {
      const token = localStorage.getItem('token');
      // For now, store in localStorage. Later can be moved to backend API
      const stored = localStorage.getItem('reusableHeaders');
      if (stored) {
        setSavedHeaders(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error loading saved headers:', error);
    }
  };

  const saveHeader = async () => {
    if (!currentHeaderBlock || !headerName.trim()) return;

    try {
      setLoading(true);
      const newHeader: ReusableHeader = {
        _id: `header-${Date.now()}`,
        name: headerName.trim(),
        headerBlock: currentHeaderBlock
      };

      const updated = [...savedHeaders, newHeader];
      localStorage.setItem('reusableHeaders', JSON.stringify(updated));
      setSavedHeaders(updated);
      setHeaderName('');
      setShowSaveDialog(false);
    } catch (error) {
      console.error('Error saving header:', error);
      alert('Failed to save header');
    } finally {
      setLoading(false);
    }
  };

  const deleteHeader = (id: string) => {
    const updated = savedHeaders.filter(h => h._id !== id);
    localStorage.setItem('reusableHeaders', JSON.stringify(updated));
    setSavedHeaders(updated);
  };

  const handleSelectHeader = (header: ReusableHeader) => {
    // Clone the header block to avoid reference issues
    const clonedBlock = JSON.parse(JSON.stringify(header.headerBlock));
    clonedBlock.id = `header-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    onSelectHeader(clonedBlock);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className={`
          text-sm font-medium
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Reusable Headers
        </h4>
        {currentHeaderBlock && (
          <button
            onClick={() => setShowSaveDialog(true)}
            className={`
              p-1 rounded hover:bg-opacity-80
              ${isDarkMode ? 'text-accent-400 hover:bg-gray-700' : 'text-accent-600 hover:bg-gray-100'}
            `}
            title="Save current header"
          >
            <Save className="w-4 h-4" />
          </button>
        )}
      </div>

      {savedHeaders.length > 0 ? (
        <div className="space-y-2">
          {savedHeaders.map(header => (
            <div
              key={header._id}
              className={`
                flex items-center justify-between p-2 rounded
                ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'}
                cursor-pointer
              `}
              onClick={() => handleSelectHeader(header)}
            >
              <span className={`
                text-sm
                ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
              `}>
                {header.name}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (header._id && window.confirm('Delete this reusable header?')) {
                    deleteHeader(header._id);
                  }
                }}
                className={`
                  p-1 rounded hover:bg-opacity-80
                  ${isDarkMode ? 'text-error-400 hover:bg-gray-700' : 'text-error-600 hover:bg-gray-200'}
                `}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className={`
          text-xs text-center py-4
          ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
        `}>
          No saved headers yet
        </p>
      )}

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className={`
          fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50
        `}>
          <div className={`
            rounded-lg shadow-xl p-6 max-w-md w-full m-4
            ${isDarkMode ? 'bg-gray-800' : 'bg-white'}
          `}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`
                text-lg font-semibold
                ${isDarkMode ? 'text-white' : 'text-gray-900'}
              `}>
                Save Header
              </h3>
              <button
                onClick={() => {
                  setShowSaveDialog(false);
                  setHeaderName('');
                }}
                className={`
                  text-gray-500 hover:text-gray-700
                  ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : ''}
                `}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`
                  block text-sm font-medium mb-1
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                `}>
                  Header Name
                </label>
                <input
                  type="text"
                  value={headerName}
                  onChange={(e) => setHeaderName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && saveHeader()}
                  className={`
                    w-full px-3 py-2 border rounded-md
                    ${isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  placeholder="e.g., Company Header"
                  autoFocus
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={saveHeader}
                  disabled={!headerName.trim() || loading}
                  className={`
                    flex-1 px-4 py-2 rounded-md
                    ${isDarkMode 
                      ? 'bg-accent-600 hover:bg-accent-700 text-white' 
                      : 'bg-accent-500 hover:bg-accent-600 text-white'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowSaveDialog(false);
                    setHeaderName('');
                  }}
                  className={`
                    flex-1 px-4 py-2 border rounded-md
                    ${isDarkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
